/**
 * Página de Relatório de OS Concluídas
 * 
 * Exibe relatório exclusivo de Ordens de Serviço concluídas
 * com total de horas trabalhadas
 * 
 * @author Equipe GestaoeCRM
 * @date 02/12/2025
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Download, RefreshCw, CheckCircle } from 'lucide-react';
import { osReportsApi, OSCompletedReport } from '../services/osReportsApi';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function OSCompletedReportPage() {
  // Buscar relatório de OS concluídas
  const { data: reportData, isLoading, refetch } = useQuery<OSCompletedReport>({
    queryKey: ['os-completed-report'],
    queryFn: () => osReportsApi.getOSCompletedReport(),
  });

  const exportToPDF = async () => {
    try {
      toast.loading('Gerando PDF...', { id: 'pdf-export' });
      
      const element = document.getElementById('report-content');
      if (!element) {
        throw new Error('Conteúdo do relatório não encontrado');
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgScaledWidth = imgWidth * ratio;
      const imgScaledHeight = imgHeight * ratio;
      const marginX = (pdfWidth - imgScaledWidth) / 2;
      const marginY = (pdfHeight - imgScaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', marginX, marginY, imgScaledWidth, imgScaledHeight);
      
      const date = new Date().toISOString().split('T')[0];
      pdf.save(`relatorio-os-concluidas-${date}.pdf`);
      
      toast.success('PDF gerado com sucesso!', { id: 'pdf-export' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF', { id: 'pdf-export' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Relatório de OS Concluídas
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Visualize todas as Ordens de Serviço concluídas com total de horas trabalhadas
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
          <button
            onClick={exportToPDF}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass p-6">
        <div className="flex items-center space-x-2 mb-4">
          <CheckCircle className="h-6 w-6 text-green-500" />
          <h3 className="text-lg font-bold text-text-primary">Resumo</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-text-muted">Total de OS Concluídas</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.totalOS}</p>
          </div>
          <div>
            <p className="text-sm text-text-muted">Total de Horas Trabalhadas</p>
            <p className="text-2xl font-bold text-green-500">{reportData.summary.formattedTotalHours}</p>
          </div>
          {reportData.summary.formattedTotalEstimatedHours && (
            <div>
              <p className="text-sm text-text-muted">Total de Horas Estimadas</p>
              <p className="text-2xl font-bold text-text-primary">{reportData.summary.formattedTotalEstimatedHours}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-text-muted">Média de Horas por OS</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.formattedAverageHours}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo do Relatório */}
      <div id="report-content" className="bg-background-glass backdrop-blur-glass shadow rounded-glass p-6">
        <h3 className="text-lg font-bold text-text-primary mb-4">OS Concluídas</h3>
        
        {reportData.report.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-muted">Nenhuma OS concluída encontrada</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reportData.report.map((os) => (
              <div key={os.id} className="bg-background-glass-hover rounded-button p-4 border-l-4 border-green-500">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h5 className="font-bold text-text-primary">{os.title}</h5>
                    <p className="text-sm text-text-muted mt-1">{os.description}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-lg font-bold text-green-500">{os.formattedHours}</p>
                    <p className="text-xs text-text-muted">horas trabalhadas</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm">
                  <div>
                    <p className="text-text-muted">Tipo:</p>
                    <p className="font-bold text-text-primary">{os.type}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Prioridade:</p>
                    <p className="font-bold text-text-primary">{os.priority}</p>
                  </div>
                  {os.companyArea && (
                    <div>
                      <p className="text-text-muted">Área:</p>
                      <p className="font-bold text-text-primary">{os.companyArea.name}</p>
                    </div>
                  )}
                  {os.formattedStartDate && (
                    <div>
                      <p className="text-text-muted">Data de Início:</p>
                      <p className="font-bold text-text-primary">{os.formattedStartDate}</p>
                    </div>
                  )}
                  {os.formattedEndDate && (
                    <div>
                      <p className="text-text-muted">Data de Conclusão:</p>
                      <p className="font-bold text-text-primary">{os.formattedEndDate}</p>
                    </div>
                  )}
                  {os.formattedEstimatedHours && (
                    <div>
                      <p className="text-text-muted">Horas Estimadas:</p>
                      <p className="font-bold text-text-primary">{os.formattedEstimatedHours}</p>
                    </div>
                  )}
                  {os.demand && (
                    <div>
                      <p className="text-text-muted">Demanda:</p>
                      <p className="font-bold text-text-primary">{os.demand.title}</p>
                    </div>
                  )}
                  {os.project && (
                    <div>
                      <p className="text-text-muted">Projeto:</p>
                      <p className="font-bold text-text-primary">{os.project.name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-text-muted">Responsável:</p>
                    <p className="font-bold text-text-primary">{os.user.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

